import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  packagingRuleService,
  type PackagingRuleCreateRequest,
  type PackagingRuleListFilters,
  type PackagingRuleResolveParams,
  type PackagingRuleSupersedeRequest,
  type ApprovedCartonsResponse,
} from '../../services/api/packagingRuleService';

/**
 * Spec pack-analysis-approved-cartons-admin Req 1 — two-step load:
 *   1. Resolve scope → profile id (or null if no profile exists for the scope)
 *   2. If profile, fetch its approved-cartons sub-resource.
 * Returns null `profile` when the scope has no active profile yet — the
 * page should render the empty-state with the "Create Profile" CTA.
 */
export interface LoadApprovedCartonsResult {
  profileMeta: ApprovedCartonsResponse['profileMeta'] | null;
  approvedCartons: ApprovedCartonsResponse['approvedCartons'];
}

export const loadApprovedCartonsForScope = createAsyncThunk<
  LoadApprovedCartonsResult,
  PackagingRuleResolveParams
>(
  'packagingRule/loadApprovedCartonsForScope',
  async (params, { rejectWithValue }) => {
    try {
      const profile = await packagingRuleService.resolve(params);
      if (!profile) {
        return { profileMeta: null, approvedCartons: [] };
      }
      // Pass buyer scope so the BE surfaces buyer-scope description/category
      // overrides per row (Spec pack-analysis-approved-cartons-admin
      // scope-override extension).
      const data = await packagingRuleService.getApprovedCartons(profile.id, {
        tenantId: params.tenantId,
        buyerPartyId: params.buyerPartyId,
      });
      return { profileMeta: data.profileMeta, approvedCartons: data.approvedCartons };
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load approved cartons');
    }
  }
);

/**
 * Apply an approved-carton list change (add / remove / reorder). All three
 * mutations compile to a single Supersede call with the FULL new
 * approvedCartonTypeIds array. Spec pack-analysis-approved-cartons-admin
 * Req 2.5 / 3.2 / 4.2.
 *
 * After the supersede creates a new profile version, we CHAIN an immediate
 * fetch of the new profile's approved-cartons sub-resource using the
 * `newProfileId` returned by the supersede response. This deliberately
 * BYPASSES `resolve()` because the BE resolver has a 60s TTL cache that
 * would otherwise return the stale (now-expired) profile id for up to a
 * minute. See `feedback_packaging_rule_resolver_cache_ttl_trap.md`.
 *
 * The slice's `applyApprovedCartonsChange.fulfilled` reducer updates state
 * directly from `refreshed` — no separate `reload` dispatch needed.
 */
export type ApprovedCartonsChangeIntent = 'add' | 'remove' | 'reorder';

export const applyApprovedCartonsChange = createAsyncThunk<
  { newProfileId: number; refreshed: ApprovedCartonsResponse },
  {
    tenantId: number;
    profileId: number;
    newApprovedIds: number[];
    hadPriorList: boolean;
    /** Scope hint forwarded to the BE for resolver cache eviction. The
     * resolver caches by (tenant, buyer, supplier, channel) — without
     * supplierPartyId here, the supplier-keyed entry survives the evict
     * and the admin sees stale rows for up to 60s after a write. */
    buyerPartyId?: number | null;
    supplierPartyId?: number | null;
    /**
     * Distinguishes the user action so the service layer can suppress the
     * generic "profile superseded" toast for `reorder` clicks (which fire
     * one supersede per click and would otherwise spam the UI).
     */
    intent?: ApprovedCartonsChangeIntent;
  }
>(
  'packagingRule/applyApprovedCartonsChange',
  async (
    { tenantId, profileId, newApprovedIds, hadPriorList, buyerPartyId, supplierPartyId, intent },
    { rejectWithValue },
  ) => {
    try {
      const result = await packagingRuleService.supersedeWithApprovedList(
        tenantId, profileId, newApprovedIds, hadPriorList,
        { buyerPartyId, supplierPartyId },
        { silent: intent === 'reorder' },
      );
      // Spec pack-analysis-approved-cartons-admin Phase E perf — the BE
      // supersede composes the post-write approved-cartons payload inline
      // when it can do so WITHOUT extra SQL: only when ApprovedCartonTypeIds
      // is supplied AND no BuyerPartyId is supplied (so no scope-override
      // join is needed).
      //
      // When BuyerPartyId IS supplied (admin /approved-cartons page — the
      // typical reorder path), the BE skips the inline composition to avoid
      // an extra round-trip on the slow write-path connection (see
      // feedback_supersede_aad_cancellation_retry.md for why the write-path
      // is currently slow). FE falls back to the chained GET below, which
      // does the scope-override join on a presumably-warmer read connection.
      const inline = result.refreshedApprovedCartons;
      const refreshed: ApprovedCartonsResponse = inline
        ? {
            profileMeta: {
              profileId: inline.profileId,
              scopePath: inline.scopePath,
              effectiveFrom: inline.effectiveFrom,
              effectiveTo: inline.effectiveTo,
              createdBy: inline.createdBy,
              createdAt: inline.createdAt,
            },
            approvedCartons: inline.approvedCartons,
          }
        : await packagingRuleService.getApprovedCartons(result.newId, {
            tenantId,
            buyerPartyId,
          });
      return { newProfileId: result.newId, refreshed };
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update approved cartons');
    }
  }
);

/**
 * Spec pack-analysis-approved-cartons-admin (scope-override extension) —
 * upsert the buyer-scope display override for a single carton, then re-fetch
 * the approved-cartons list so the UI immediately reflects the new override
 * (or the cleared state). The scope hint forwards the buyer so getApprovedCartons
 * re-joins overrides.
 */
export const upsertApprovedCartonOverride = createAsyncThunk<
  { refreshed: ApprovedCartonsResponse },
  {
    tenantId: number;
    cartonTypeId: number;
    buyerPartyId: number;
    profileId: number;
    descriptionOverride: string | null;
    categoryOverride: string | null;
  }
>(
  'packagingRule/upsertApprovedCartonOverride',
  async (
    { tenantId, cartonTypeId, buyerPartyId, profileId, descriptionOverride, categoryOverride },
    { rejectWithValue },
  ) => {
    try {
      await packagingRuleService.upsertCartonScopeOverride(
        tenantId, cartonTypeId, buyerPartyId, descriptionOverride, categoryOverride,
      );
      const refreshed = await packagingRuleService.getApprovedCartons(profileId, {
        tenantId,
        buyerPartyId,
      });
      return { refreshed };
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to save carton override');
    }
  }
);

/**
 * Cache key used by `packagingRuleSlice.resolved` so duplicate consumers for
 * the same (tenant, buyer, supplier, channel) tuple share state.
 */
export function packagingRuleCacheKey(p: PackagingRuleResolveParams): string {
  return [
    p.tenantId,
    p.buyerPartyId ?? '_',
    p.supplierPartyId ?? '_',
    p.channelCode ?? '_',
    p.asOfUtc ?? '_',
  ].join('|');
}

export const fetchPackagingRuleList = createAsyncThunk(
  'packagingRule/fetchList',
  async (filters: PackagingRuleListFilters, { rejectWithValue }) => {
    try {
      return await packagingRuleService.list(filters);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch packaging-rule rows');
    }
  }
);

export const resolvePackagingRule = createAsyncThunk(
  'packagingRule/resolve',
  async (params: PackagingRuleResolveParams, { rejectWithValue }) => {
    try {
      const data = await packagingRuleService.resolve(params);
      return { key: packagingRuleCacheKey(params), data };
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to resolve packaging-rule profile');
    }
  }
);

export const createPackagingRule = createAsyncThunk(
  'packagingRule/create',
  async (
    { tenantId, request }: { tenantId: number; request: PackagingRuleCreateRequest },
    { rejectWithValue }
  ) => {
    try {
      return await packagingRuleService.create(tenantId, request);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create packaging-rule profile');
    }
  }
);

export const supersedePackagingRule = createAsyncThunk(
  'packagingRule/supersede',
  async (
    { tenantId, id, request }: { tenantId: number; id: number; request: PackagingRuleSupersedeRequest },
    { rejectWithValue }
  ) => {
    try {
      return await packagingRuleService.supersede(tenantId, id, request);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to supersede packaging-rule profile');
    }
  }
);

export const deletePackagingRule = createAsyncThunk(
  'packagingRule/delete',
  async (
    { tenantId, id }: { tenantId: number; id: number },
    { rejectWithValue }
  ) => {
    try {
      await packagingRuleService.remove(tenantId, id);
      return id;
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete packaging-rule profile');
    }
  }
);
